import { useState, useCallback } from 'react';
import { useToast } from '@/context/ToastContext';
import {
  Sheet,
  SheetContentResizable,
  SheetFooter,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet';
import { Button } from '@/components/ui/button';
import { useLocalStorageValue } from '@react-hookz/web';
import {
  EntityDetailTabsProvider,
  EntityDetailTabsHeader,
  EntityDetailTabsContent,
  useEntityDetailTabsContext,
} from '@/components/accounts-payable/shared/entity-detail-tabs';
import { SystemEntity } from '@erp/shared-types';
import { Tabs } from '@/components/ui/tabs';
import { ForegroundAgentChat } from '@/components/common/ForegroundAgentChat';
import {
  InvoiceComparisonDialog,
  type ComparisonData,
  type ChartData,
} from '@/components/accounts-payable/InvoiceComparisonDialog';
import { EmailDraftEditor } from '@/components/email-draft-editor/EmailDraftEditor';
import { useEmailDraft } from '@/api/communications/useEmailDraft';
import { useAgentPanel } from '@/context/agent-panel.context';

function InvoiceDetailTabsWrapper() {
  const { activeTabId, setActiveTabId } = useEntityDetailTabsContext();
  return (
    <Tabs
      value={activeTabId}
      onValueChange={setActiveTabId}
      className="flex flex-col flex-1 min-h-0"
    >
      <SheetHeader className="shrink-0 grow-0">
        <EntityDetailTabsHeader />
      </SheetHeader>
      <div className="flex-1 min-h-0 overflow-hidden">
        <EntityDetailTabsContent />
      </div>
    </Tabs>
  );
}

export function InvoiceDetailSlidein({
  isOpen,
  onOpenChange,
  invoiceId,
}: {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  invoiceId?: string;
}) {
  const { showToast } = useToast();
  const { isOpen: isChatOpen } = useAgentPanel();
  const { value: detailViewSize, set: setDetailViewSize } =
    useLocalStorageValue<number>('invoice-detail-view-size', {
      defaultValue: 50,
    });

  const [comparisonDialogOpen, setComparisonDialogOpen] = useState(false);
  const [comparisonData, setComparisonData] = useState<ComparisonData | null>(
    null,
  );
  const [comparisonChart, setComparisonChart] = useState<ChartData | null>(
    null,
  );

  const [emailDraftEditorOpen, setEmailDraftEditorOpen] = useState(false);
  const [emailDraftId, setEmailDraftId] = useState<string | null>(null);

  const { draft: emailDraft } = useEmailDraft(emailDraftId ?? undefined);

  const handleShowComparison = useCallback(
    (payload: { comparisonData: ComparisonData; chart: ChartData }) => {
      setComparisonData(payload.comparisonData);
      setComparisonChart(payload.chart);
      setComparisonDialogOpen(true);
    },
    [],
  );

  const handleComparisonDialogClose = useCallback(() => {
    setComparisonDialogOpen(false);
    setTimeout(() => {
      setComparisonData(null);
      setComparisonChart(null);
    }, 200);
  }, []);

  const handleOpenEmailDraft = useCallback((draftId: string) => {
    setEmailDraftId(draftId);
    setEmailDraftEditorOpen(true);
  }, []);

  const handleEmailDraftSave = useCallback(async () => {
    // TODO: Implement save functionality
  }, []);

  const handleEmailDraftSend = useCallback(async () => {
    showToast({
      title: 'Email sent',
      message: 'Your email has been sent successfully.',
      color: 'success',
    });
    setEmailDraftEditorOpen(false);
    setEmailDraftId(null);
  }, [showToast]);

  const handleEmailDraftDiscard = useCallback(() => {
    setEmailDraftEditorOpen(false);
    setEmailDraftId(null);
  }, []);

  return (
    <>
      <Sheet open={isOpen} onOpenChange={onOpenChange}>
        <SheetContentResizable
          resizableProps={{
            defaultSize: detailViewSize,
            onResize: setDetailViewSize,
            className: 'flex flex-col',
          }}
        >
          <div
            className={`flex flex-col flex-1 min-h-0 transition-[margin] duration-200 ${
              isChatOpen && invoiceId ? 'mr-[400px]' : ''
            }`}
          >
            {invoiceId ? (
              <EntityDetailTabsProvider
                initialEntity={{
                  entityType: SystemEntity.AP_INVOICE,
                  entityId: invoiceId,
                }}
              >
                <InvoiceDetailTabsWrapper />
              </EntityDetailTabsProvider>
            ) : (
              <>
                <SheetHeader className="shrink-0 grow-0">
                  <SheetTitle>Invoice Details</SheetTitle>
                </SheetHeader>
                <div className="p-4 text-sm text-muted-foreground">
                  No invoice selected
                </div>
              </>
            )}
            <SheetFooter className="shrink-0 grow-0 flex items-end border-t">
              <Button
                onClick={() => {
                  onOpenChange(false);
                }}
                className="w-fit"
              >
                Close
              </Button>
            </SheetFooter>
          </div>

          {invoiceId && (
            <ForegroundAgentChat
              moduleKey="ap"
              currentPage={`/accounts-payable/invoices/${invoiceId}`}
              isEmbedded={true}
              onShowComparison={handleShowComparison}
              onOpenEmailDraft={handleOpenEmailDraft}
            />
          )}
        </SheetContentResizable>
      </Sheet>

      <InvoiceComparisonDialog
        open={comparisonDialogOpen}
        onClose={handleComparisonDialogClose}
        comparisonData={comparisonData}
        chart={comparisonChart}
      />

      {emailDraft && (
        <EmailDraftEditor
          draft={{
            id: emailDraft.id,
            to: emailDraft.to,
            cc: emailDraft.cc,
            subject: emailDraft.subject,
            content: emailDraft.content,
            htmlContent: emailDraft.htmlContent,
            isAiGenerated: emailDraft.isAiGenerated,
            status: emailDraft.status,
            sendingMailbox: emailDraft.sendingMailbox,
          }}
          mode="markdown"
          open={emailDraftEditorOpen}
          onOpenChange={setEmailDraftEditorOpen}
          onSave={handleEmailDraftSave}
          onSend={handleEmailDraftSend}
          onDiscard={handleEmailDraftDiscard}
        />
      )}
    </>
  );
}
